<script setup>
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatPrice, formatTime } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type } = defineProps({
    type: {
        type: String,
        required: false,
        default: null,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route(
                        'front.projectManagement.customer.updateCustomer',
                        {
                            tenant: props.row.original.id,
                        },
                    ),
                    target: '_self',
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'special',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Forfait',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full p-4 font-bold justify-start gap-1',
                    },
                    props.row.original.specialPackage
                        ? [
                              h('div', {
                                  innerText:
                                      props.row.original.specialPackage.name,
                                  class: 'text-xs',
                              }),
                              h('div', {
                                  innerHTML: '&nbsp;',
                                  class: 'text-lg',
                              }),
                          ]
                        : [
                              h('div', {
                                  innerHTML: '&nbsp;',
                                  class: 'text-xs',
                              }),
                              null,
                          ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seo',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SEO',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    [
                        h('div', {
                            innerHTML: '&nbsp;',
                            class: 'text-xs',
                        }),
                        props.row.original.seoPackage
                            ? h('div', {
                                  innerText: formatPrice(
                                      props.row.original.seoPackage.price,
                                  ),
                                  class: 'text-lg',
                              })
                            : null,
                    ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'sea',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SEA M-1',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    props.row.original.seaPackage
                        ? props.row.original.suggestedSeaPackage &&
                          props.row.original.suggestedSeaPackage.id !==
                              props.row.original.seaPackage.id
                            ? [
                                  h('div', {
                                      innerText: formatPrice(
                                          props.row.original.seaPackage.price,
                                      ),
                                      class: 'text-xs line-through',
                                  }),
                                  h('div', {
                                      innerText: formatPrice(
                                          props.row.original.suggestedSeaPackage
                                              .price,
                                      ),
                                      class: 'text-lg',
                                  }),
                              ]
                            : [
                                  h('div', {
                                      innerHTML: '&nbsp;',
                                      class: 'text-xs',
                                  }),
                                  h('div', {
                                      innerText: formatPrice(
                                          props.row.original.seaPackage.price,
                                      ),
                                      class: 'text-lg',
                                  }),
                              ]
                        : [
                              h('div', {
                                  innerHTML: '&nbsp;',
                                  class: 'text-xs',
                              }),
                              null,
                          ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'smo',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SMO',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    [
                        h('div', {
                            innerHTML: '&nbsp;',
                            class: 'text-xs',
                        }),
                        props.row.original.smoPackage
                            ? h('div', {
                                  innerText: formatPrice(
                                      props.row.original.smoPackage.price,
                                  ),
                                  class: 'text-lg',
                              })
                            : null,
                    ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'sma',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SMA M-1',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    props.row.original.smaPackage
                        ? props.row.original.suggestedSmaPackage &&
                          props.row.original.suggestedSmaPackage.id !==
                              props.row.original.smaPackage.id
                            ? [
                                  h('div', {
                                      innerText: formatPrice(
                                          props.row.original.smaPackage.price,
                                      ),
                                      class: 'text-xs line-through',
                                  }),
                                  h('div', {
                                      innerText: formatPrice(
                                          props.row.original.suggestedSmaPackage
                                              .price,
                                      ),
                                      class: 'text-lg',
                                  }),
                              ]
                            : [
                                  h('div', {
                                      innerHTML: '&nbsp;',
                                      class: 'text-xs',
                                  }),
                                  h('div', {
                                      innerText: formatPrice(
                                          props.row.original.smaPackage.price,
                                      ),
                                      class: 'text-lg',
                                  }),
                              ]
                        : [
                              h('div', {
                                  innerHTML: '&nbsp;',
                                  class: 'text-xs',
                              }),
                              null,
                          ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'hour',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'H (hors dev) M-1',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    [
                        props.row.original.hour
                            ? [
                                  h('div', {
                                      innerText: formatTime(
                                          props.row.original.hour.hours,
                                          props.row.original.hour.minutes,
                                      ),
                                      class: 'text-xs',
                                  }),
                                  h('div', {
                                      innerText: formatPrice(
                                          props.row.original.hour.price,
                                      ),
                                      class: 'text-lg',
                                  }),
                              ]
                            : [
                                  h('div', {
                                      innerHTML: '&nbsp;',
                                      class: 'text-xs',
                                  }),
                                  null,
                              ],
                    ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'shoppingfeed',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SF',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    [
                        h('div', {
                            innerHTML: '&nbsp;',
                            class: 'text-xs',
                        }),
                        props.row.original.shoppingfeedPackage
                            ? h('div', {
                                  innerText: formatPrice(
                                      props.row.original.shoppingfeedPackage
                                          .price,
                                  ),
                                  class: 'text-lg',
                              })
                            : null,
                    ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'hosting',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Hébergement',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    [
                        h('div', {
                            innerHTML: '&nbsp;',
                            class: 'text-xs',
                        }),
                        props.row.original.hostingPackage
                            ? h('div', {
                                  innerText: formatPrice(
                                      props.row.original.hostingPackage.price,
                                  ),
                                  class: 'text-lg',
                              })
                            : null,
                    ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'modules',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Modules',
                }),
            cell: (props) =>
                props.row.original.emailings ||
                props.row.original.emailingAutomation ||
                props.row.original.customerTickets
                    ? h(
                          'div',
                          {
                              class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                          },
                          [
                              ...(props.row.original.emailings
                                  ? [
                                        h('div', {
                                            innerText:
                                                'Emailings M-1 - ' +
                                                props.row.original
                                                    .lastMonthDate,
                                            class: 'text-xs',
                                        }),
                                        h('div', {
                                            innerText: formatPrice(
                                                props.row.original.emailings,
                                            ),
                                            class: 'text-lg',
                                        }),
                                    ]
                                  : []),
                              ...(props.row.original.emailingAutomation
                                  ? [
                                        h('div', {
                                            innerText:
                                                'Emailing automation M-1 - ' +
                                                props.row.original
                                                    .lastMonthDate,
                                            class: 'text-xs',
                                        }),
                                        h('div', {
                                            innerText: formatPrice(
                                                props.row.original
                                                    .emailingAutomation,
                                            ),
                                            class: 'text-lg',
                                        }),
                                    ]
                                  : []),
                              ...(props.row.original.customerTickets
                                  ? [
                                        h('div', {
                                            innerText:
                                                'Tickets M-1 - ' +
                                                props.row.original
                                                    .lastMonthDate,
                                            class: 'text-xs',
                                        }),
                                        h('div', {
                                            innerText: formatPrice(
                                                props.row.original
                                                    .customerTickets,
                                            ),
                                            class: 'text-lg',
                                        }),
                                    ]
                                  : []),
                          ],
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'dev',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'DEV M-1',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    props.row.original.dev
                        ? [
                              h('div', {
                                  innerText: formatTime(
                                      props.row.original.dev.hours,
                                      props.row.original.dev.minutes,
                                  ),
                                  class: 'text-xs',
                              }),
                              h('div', {
                                  innerText: formatPrice(
                                      props.row.original.dev.price,
                                  ),
                                  class: 'text-lg',
                              }),
                          ]
                        : [
                              h('div', {
                                  innerHTML: '&nbsp;',
                                  class: 'text-xs',
                              }),
                              null,
                          ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastMonthTurnoverExcludingTax',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA HT M-1',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    props.row.original.lastMonthTurnoverExcludingTax
                        ? [
                              h('div', {
                                  innerText: props.row.original.lastMonthDate,
                                  class: 'text-xs',
                              }),
                              h('div', {
                                  innerHTML: formatPrice(
                                      props.row.original
                                          .lastMonthTurnoverExcludingTax,
                                      false,
                                  ),
                                  class: 'text-lg',
                              }),
                          ]
                        : [
                              h('div', {
                                  innerHTML: '&nbsp;',
                                  class: 'text-xs',
                              }),
                              null,
                          ],
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'last2MonthsTurnoverExcludingTax',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA HT M-2',
                }),
            cell: (props) =>
                h(
                    'div',
                    {
                        class: 'flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1',
                    },
                    props.row.original.last2MonthsTurnoverExcludingTax
                        ? [
                              h('div', {
                                  innerText: props.row.original.last2MonthsDate,
                                  class: 'text-xs',
                              }),
                              h('div', {
                                  innerHTML: formatPrice(
                                      props.row.original
                                          .last2MonthsTurnoverExcludingTax,
                                      false,
                                  ),
                                  class: 'text-lg',
                              }),
                          ]
                        : [
                              h('div', {
                                  innerHTML: '&nbsp;',
                                  class: 'text-xs',
                              }),
                              null,
                          ],
                ),
            enableSorting: false,
        }),
    ],
    url: route('front.projectManagement.apikom.postInvoicing'),
    data: {
        type,
    },
    columnVisibility: {
        seo: type === 'e-commerce' || type === 'showcase' || !type,
        sea: type === 'e-commerce' || type === 'showcase' || !type,
        smo: type === 'e-commerce' || type === 'showcase' || !type,
        sma: type === 'e-commerce' || type === 'showcase' || !type,
        modules: type === 'e-commerce' || type === 'showcase' || !type,
        shoppingfeed: type === 'e-commerce' || !type,
        lastMonthTurnoverExcludingTax: type === 'e-commerce' || !type,
        last2MonthsTurnoverExcludingTax: type === 'e-commerce' || !type,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
